import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SquareArrowOutUpRight, FileText } from "lucide-react";

import PageHeader from "../paragraphHeader";
import NotFoundImage from "../NotFound";
import { galleryFetch } from "../../services/api/Gallery";

interface PdfFile {
  name: string;
  url: string;
}

interface GalleryItem {
  id: number;
  category_name: string | null;
  title: string | null;
  slug: string | null;
  image: string | null;
  pdf: PdfFile[] | null;
}

export function DhyanchandAwardsPage() {
  const { slug } = useParams<{ slug: string }>();

  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGallery();
  }, [slug]);

  const fetchGallery = async () => {
    try {
      setLoading(true);

      const endpoint = slug ? `social-galleries/${slug}` : "media";
      const response = await galleryFetch(endpoint);

      if (response?.success) {
        setGallery(response.data?.data[0]?.children || []);
      } else {
        setGallery([]);
      }
    } catch (error) {
      console.error("Failed to fetch gallery:", error);
      setGallery([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full mt-10">
      <PageHeader
        title={
          <h1 className="font-semibold text-[#1F4D8F] uppercase">
            {slug?.replace(/-/g, " ") || "Sports Events"}
          </h1>
        }
        subtitle={
          <>
            Explore{" "}
            <span className="text-blue-500 font-bold">Sports Events</span>
          </>
        }
        paragraph="Explore Haryana's premier sports events and championships."
        backgroundImage=""
        align="center"
        showOlympicsIcon={true}
      />

      <div className="max-w-7xl mx-auto rounded-2xl p-8">
        <div className="mb-10">
          <h2 className="text-3xl font-bold text-slate-800 capitalize">
            Explore Our {slug?.replace(/-/g, " ") || "Sports Events"}
          </h2>

          <div className="w-20 h-1 bg-yellow-400 rounded-full mt-3" />
        </div>

        {loading ? (
          <p className="text-center text-gray-500">Loading...</p>
        ) : gallery.length === 0 ? (
          <NotFoundImage />
        ) : (
          <div className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-4">
            {gallery.map((item) => {
              const pdfFiles = Array.isArray(item.pdf) ? item.pdf : [];
              const pdfUrl = pdfFiles[0]?.url || null;
              const imageUrl = item.image || null;

              // PDF gets priority when clicking the card.
              const openUrl = pdfUrl || imageUrl;

              const displayTitle =
                item.title ||
                item.slug ||
                item.category_name ||
                pdfFiles[0]?.name ||
                "Open File";

              return (
                <a
                  key={item.id}
                  href={openUrl || "#"}
                  target={openUrl ? "_blank" : undefined}
                  rel={openUrl ? "noopener noreferrer" : undefined}
                  className={`block ${!openUrl ? "pointer-events-none" : ""}`}
                >
                  <div className="relative rounded-sm shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={displayTitle}
                        className="w-full h-[260px] object-cover transition-all duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div
                        className="w-full h-[200px] sm:h-[230px] md:h-[260px]
 flex flex-col items-center justify-center px-4 bg-gray-50"
                      >
                        <FileText className="w-20 h-20 mb-4 text-red-600" />

                        {pdfUrl && (
                          <span className="mt-2 text-sm text-blue-600">
                            View PDF
                          </span>
                        )}
                      </div>
                    )}

                    {/* Title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-12">
                      <p className="text-white font-semibold line-clamp-2">
                        {displayTitle}
                      </p>
                    </div>

                    {/* Hover overlay */}
                    {openUrl && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                          <SquareArrowOutUpRight className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    )}
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
